/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import classnames from 'classnames';
import { action, computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    getTelemetryObject, IModuleProps, ITelemetryContent, KeyCodes
} from '@msdyn365-commerce-modules/utilities';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import {
    Address, AddressPurpose, CommerceProperty, CountryRegionInfo, isNullOrWhitespace, StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy';
import { Cart, DeliveryOption } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { AutoSuggest } from '../../common/address/address-autosuggest';
import { IAutoSuggestOptions } from '../../common/address/address-autosuggest.data';
import { AddressCommon } from '../../common/address/address-common';
import { AddressFormat } from '../../common/address/address-format';
import { AddressItemType, IAddressDropdownsData } from '../../common/address/address-format.data';
import { AddressMetaData } from '../../common/address/address-meta-data';
import {
    AddressOperation, AddressType, IAddressResource, IAddressResponse
} from '../../common/address/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../../common/address/components/address-add';
import { AddressSelect, IAddressSelectProps } from '../../common/address/components/address-select';
import { AddressShow, IAddressShowProps } from '../../common/address/components/address-show';
import AddressSearch, { IFormattedAddressDetails } from '../../components/citta-address-search/citta-address-search';
import getAddressSaveInput, { createAddressSaveInput } from '../../data-actions/address/address-save';
import { dXC_GetDeliveryOptionsAsync } from '../../data-actions/generated/DataActionExtension.g';


import { IGetFreightChargesServiceResponse } from '../../models/GetFreightChargesServiceResponse';
import { IContext } from '@msdyn365-commerce/retail-proxy';

import { dXC_GetFreightChargesForPDPAsync } from '../../data-actions/generated/DataActionExtension.g';
import { IDXC_GetFreightChargesServiceResponse, IDXC_ItemLine } from '../../data-actions/generated/DataServiceEntities.g';

import { ICittaCheckoutShippingAddressData } from './citta-checkout-shipping-address.data';
import { ICittaCheckoutShippingAddressProps } from './citta-checkout-shipping-address.props.autogenerated';
import { IClickAndCollectAddress } from './click-and-collect-address';
export interface ICheckoutAddressProps extends ICittaCheckoutShippingAddressProps<ICittaCheckoutShippingAddressData>, IModuleStateProps { }
import getShippingTypeInput, { craeteShippingTypeInput } from '../../data-actions/click-and-collect/shipping-types';
import { _getProductDetailsWithAttributes, _getProductInventory, IHSOProductDetails } from './hso-helpers';
import { CartLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IActionContext } from '@msdyn365-commerce/core';

export interface ICheckoutShippingAddressViewState {
    isShowAddress: boolean;
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface ICheckoutShippingAddressViewProps extends ICheckoutAddressProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutShippingAddressViewState;
    CheckoutShippingAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddress: IAddressShowProps;
    showAddressSelect: IAddressSelectProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    showToggleOptions?: React.ReactNode;
    deliveryNotes?: JSX.Element;
    regionAU?: JSX.Element;
    clickAndCollectStores?: JSX.Element | null;
    selectedMode?: string;
    storeName?: string;
}
interface IClickAndCollectState {
    address: Address;
    selectedOption: string;
    customerDefaultAddress: Address;
    selectedStoreName: string;
    selectedStoreCode: string;
    selectedStore: number;
    name: string;
    deliveryNotes: string;
    regionAU6or7: boolean;
    isFurniture: boolean;
}
const enum deliveryType {
    delivery = 'opt1',
    clickAndCollect = 'opt2',
    // express = 'opt3'
}

/**
 *
 * Address component
 * @extends {React.Component<ICittaCheckoutShippingAddressProps<ICittaCheckoutShippingAddressData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutShippingAddress extends React.Component<ICheckoutAddressProps, IClickAndCollectState > {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private addUpdateAddress: Address;
    @observable private countryRegionId: string = 'NZL';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;
    @observable private addressListSelectedAddress: Address = {};
    @observable private manualAddress: boolean;
    @observable private deliveryOptions: DeliveryOption[] = [];

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private defaultAddressType: number = this.props.context.app.config.defaultAddressTypeValue || 7;
    private telemetryContent?: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    private multiplePickupStoreSwitchName: string = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    private retailMultiplePickUpOptionEnabled?: boolean = false;
    private _storePickerSelectRef: React.RefObject<HTMLSelectElement>;
    private deliveryNotesRef: React.RefObject<HTMLTextAreaElement>;
    private nzlCountryRegionId: string = 'NZL';
    private isNZ: boolean = false;
    private shippingName: string;
    private contactName: string;
    private addressDropdownsData:IAddressDropdownsData = {};
    private showAddressSearch: boolean = true;
    private isClickAndCollectOrder: boolean = false;
    private lastStoreName: string|undefined;
    private lastStoreCode: number;
    constructor(props: ICheckoutAddressProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;
        this.shippingName = data.checkout.result && data.checkout.result.shippingAddress && data.checkout.result.shippingAddress.Name || '';
        this.contactName = this._getContactNameFromCartExtensions();
        this.isRegionAU6and7 = this.isRegionAU6and7.bind(this);
        this.cityList = this.cityList.bind(this)

        this.state = {
            address: {},
            selectedOption:  this._getExtensionPropertyValue('DELIVERYTYPE') || deliveryType.delivery,
            deliveryNotes: this._getExtensionPropertyValue('DELIVERYNOTES') || '',
            customerDefaultAddress: {},
            selectedStoreName: this._getExtensionPropertyValue('SELECTEDSTORENAME') || '',
            selectedStoreCode: '',
            selectedStore: parseInt(this._getExtensionPropertyValue('SELECTEDSTOREINDEX') || '0', 10) || 0,
            name: this.shippingName || this.contactName || '',
            regionAU6or7: false,
            isFurniture: false
        };
        this.addUpdateAddress = {};
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(this.countryRegions, new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()), this.addressPurposes);
        this.validationError = {};
        this.retailMultiplePickUpOptionEnabled = data?.featureState?.result?.
        find(feature => feature.Name === this.multiplePickupStoreSwitchName)?.IsEnabled;
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this._storePickerSelectRef = React.createRef();
        this.deliveryNotesRef = React.createRef();
        this.isNZ = this.selectedAddress && this.selectedAddress.ThreeLetterISORegionName === this.nzlCountryRegionId || false;
        // this.manualAddress = false;
        this.manualAddress = true;
        this.lastStoreName = '';
        this.lastStoreCode= parseInt(this._getExtensionPropertyValue('SELECTEDSTOREINDEX') || '0', 10) || 0;
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: {
                        channel
                    }
                }
            },
            config: {
                autoSuggestionEnabled,
                autoSuggestOptions
            }
        } = this.props;

        this.props.data.checkout.then((result) => {
            if(result.status === 'READY') {
                this._setDefaultCountryRegionId();
                this._initModuleState();
            }
        });
        // this.getDeliveryOptions({}).catch(error => telemetry.error(`error in getDeliveryOptions ${error}`));
        this.isFurniture();
        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(telemetry, options, channel?.BingMapsApiKey, channel?.ChannelCountryRegionISOCode, channel?.DefaultLanguageId);

            // Customer doesn't have any address. Then add view will be loaded directly. Code for the same to handle that
            if(this.props.data.storeSelectorStateManager.result && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
                await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
            }

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.loadMapApi && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
                async () => {
                    await this._attachMapAutoSuggest();
                }
            );
        }
    }

    public async componentDidUpdate(): Promise<void> {
        const {name} = this.state;
        this.contactName = this._getContactNameFromCartExtensions() || '';
        if (!isNullOrWhitespace(this.contactName) && isNullOrWhitespace(name)) {
            const {data} = this.props;
            this.shippingName = data.checkout.result && data.checkout.result.shippingAddress && data.checkout.result.shippingAddress.Name || '';
            const _name= this.shippingName || this.contactName;
            const _phone = data.checkout.result && data.checkout.result.shippingAddress && data.checkout.result.shippingAddress.Phone || this._getContactPhoneFromCartExtensions() || '';
            this.setState({name: _name});
            set(this.addUpdateAddress, {
                ['Name']: _name
            });
            set(this.addUpdateAddress, {
                ['Phone']: _phone
            });
        }

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    public render(): JSX.Element | null {
        if (!this._canShip()) {
            return null;
        }

        const { renderView, config, context } = this.props;
        const {selectedOption} = this.state;
        const defaultViewState = {
            isShowAddress: this.currentOperation === AddressOperation.Show && this.selectedAddress,
            isShowAddresList: this.currentOperation === AddressOperation.List && this.customerAddresses.length > 0,
            isShowAddOrUpdateAddress: this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update
        };

        if (context && context.request && context.request.user && context.request.user.isAuthenticated) {
            if(selectedOption === deliveryType.clickAndCollect && this.currentOperation === AddressOperation.List) {
                defaultViewState.isShowAddresList = false;
                defaultViewState.isShowAddOrUpdateAddress = true;
            }
        }
        let localSelectedMode : string | undefined = '';
        if(this.state.selectedOption === deliveryType.delivery)
        {
            localSelectedMode = config.deliveryOption1Text;
        }
        else if(this.state.selectedOption === deliveryType.clickAndCollect)
        {
            localSelectedMode = config.deliveryOption2Text;
        }
        // else if(this.state.selectedOption === deliveryType.express)
        // {
        //     localSelectedMode = config.deliveryOption3Text;
        // }

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            addressListSelectedAddress: this.addressListSelectedAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: defaultViewState,
            CheckoutShippingAddress: {
                moduleProps: this.props,
                className: classnames(`ms-checkout-shipping-address shipping-address ${selectedOption}`, config.className)
            },
            showAddress: this._renderShowAddress(),
            showAddressSelect: this._renderSelectAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
            showToggleOptions: this.currentOperation !== AddressOperation.Show && this._renderRadioButton(),
            deliveryNotes: this._renderDeliveryNotes(),
            regionAU: this._renderRegionAUError(),
            clickAndCollectStores: this._renderClickAndCollectStores(),
            selectedMode : localSelectedMode,
            storeName:this.state.selectedStoreName, 
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private _renderSearchAddress = (): React.ReactElement | null => {
        if (this.showAddressSearch && this.isNZ) {
            return (
                <AddressSearch
                    context={this.props.context}
                    manualAddress={this.manualAddress}
                    addressSelectedCallback={this._handleSearchAddressSelection}
                    toggleManualAddressCallback={this._toggleManualAddressInput}
                />
            );
        }
        return null;
    }

    private _renderRadioButton(): React.ReactNode {
        const { enableClickAndCollect, deliveryOption1Text, deliveryOption2Text} = this.props.config;
        const {selectedOption} = this.state;
        const option1 = deliveryOption1Text || '';
        const option2 = deliveryOption2Text || '';
        // const option3 = deliveryOption3Text || '';
        const isOpt1SelectedClass = (selectedOption.toLocaleLowerCase() === deliveryType.delivery) ? 'selected' :'';
        const isOpt2SelectedClass = (selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect) ? 'selected' :'';
        // const isOpt3SelectedClass = (selectedOption.toLocaleLowerCase() === deliveryType.express) ? 'selected' :'';
        const showClickAndCollect = enableClickAndCollect || false;
        const { ariaLabelText = 'Delivery options' } = this.props.resources;
        return (
            <div className='click-and-collect-module'>
                <div className='options' aria-label={ariaLabelText}>
                    <ul className='deliveryoptions' role='radiogroup'>
                        {option1 && (
                            <li
                                className={`${selectedOption} ${isOpt1SelectedClass}`}
                                role='radio'
                                aria-checked={selectedOption === deliveryType.delivery}
                                aria-posinset={1}
                                tabIndex={0}
                                onKeyDown={this._onOptionChange(deliveryType.delivery)}
                                onClick={this._onOptionChange(deliveryType.delivery)}
                            >
                                <div className='position-relative form-check'>
                                <span />{option1}
                                </div>
                            </li>)
                        }
                        {
                            // this.isClickAndCollectOrder && showClickAndCollect &&
                            this.state.isFurniture && showClickAndCollect &&
                        (
                            <li
                                className={`${selectedOption} ${isOpt2SelectedClass}`}
                                role='radio'
                                aria-checked={selectedOption === deliveryType.clickAndCollect}
                                aria-posinset={2}
                                tabIndex={0}
                                onKeyDown={this._onOptionChange(deliveryType.clickAndCollect)}
                                onClick={this._onOptionChange(deliveryType.clickAndCollect)}
                            >
                                <div className='position-relative form-check'>
                                <span />{option2}
                                </div>
                            </li>)}
                        {/* {option3 && this.state.isFurniture && (
                            <li
                                className={`${selectedOption} ${isOpt3SelectedClass}`}
                                role='radio'
                                aria-checked={selectedOption === deliveryType.express}
                                aria-posinset={1}
                                tabIndex={0}
                                onKeyDown={this._onOptionChange(deliveryType.express)}
                                onClick={this._onOptionChange(deliveryType.express)}
                            >
                                <div className='position-relative form-check'>
                                <span />{option3}--{456}
                                </div>
                            </li>)

                        } */}
                    </ul>
                </div>
            </div>
        );
    }


    private isFurniture = async():Promise<void> => {
        const currentCart = (this.props.data.cart.result?.cart as Cart);
                
        if(currentCart.CartLines)
        {
            const productDetailsPromise = this.getProductDetails(
                currentCart.CartLines,
                this.props.context.request.apiSettings.channelId,
                this.props.context.actionContext
            );
            const productDetails = await productDetailsPromise;
            let hasFreightItem = false;
            productDetails.forEach((productDetail, index, array) => {
                const freightItemAttr = productDetail.AttributeValues && productDetail.AttributeValues.find(attr => attr.Name === "Freight Item");
                if ((freightItemAttr && freightItemAttr.BooleanValue)){
                    hasFreightItem = true;
                }
            });
            this.setState({isFurniture: !hasFreightItem});
        }
    }
    // @ts-ignore
    private getDeliveryOptions = async (shippingAddress: Address): Promise<void> => {
        const { context, resources } = this.props;
        const cartId = (this.props.data.cart.result?.cart as Cart).Id;
        const clickAndCollectDeliveryModeCode = this.props.context.app.config.clickAndCollectDeliveryModeCode || 'CLICK&COLL';
        if (context) {
            this.deliveryOptions = [];
            this.clearError();

            try {
                const response = await dXC_GetDeliveryOptionsAsync({ callerContext: context.actionContext, queryResultSettings: {} }, cartId, shippingAddress, 0);
                if (response && response.length) {
                    this.deliveryOptions = response.filter(deliveryOption => deliveryOption.Code !== clickAndCollectDeliveryModeCode); // Filter out the 'Customer pickup' option
                    this.isClickAndCollectOrder = response.filter(deliveryOption => deliveryOption.Code === clickAndCollectDeliveryModeCode).length > 0 && true || false;
                }

                this.setState({isFurniture: this.isClickAndCollectOrder});

                const currentCart = (this.props.data.cart.result?.cart as Cart);
                
                if(currentCart.CartLines)
                {
                    const productDetailsPromise = this.getProductDetails(
                        currentCart.CartLines,
                        this.props.context.request.apiSettings.channelId,
                        this.props.context.actionContext
                    );
                    const productDetails = await productDetailsPromise;
                    let hasFreightItem = false;
                    productDetails.forEach((productDetail, index, array) => {
                        const freightItemAttr = productDetail.AttributeValues && productDetail.AttributeValues.find(attr => attr.Name === "Freight Item");
                        if ((freightItemAttr && freightItemAttr.BooleanValue)){
                            hasFreightItem = true;
                        }
                    });
                    this.setState({isFurniture: !hasFreightItem});
                }
                
                
            } catch (error) {
                this.setError(resources.genericErrorMessage);
            }

            if (this.deliveryOptions.length === 0) {
                this.setError(resources.deliveryOptionsNotFound);
            }
        }
    };
    private _renderClickAndCollectStores = (): JSX.Element | null => {
        const {selectedStore, selectedOption} = this.state;
        if(this.currentOperation !== AddressOperation.Show && selectedOption == deliveryType.clickAndCollect) {
            return (
                <div className={`click-and-collect-option ${selectedOption}`}>
                    <div className='addresssearch'>
                        <label className='headinglabel'>{this.props.resources.addressSearchHeadingLabel}</label>
                        <select
                            name='storeslist'
                            aria-label='Store List'
                            onChange={this._onAddressChange(deliveryType.clickAndCollect)}
                            ref={this._storePickerSelectRef}
                        >
                            <option aria-selected='false' value='0' />
                            {this.props.config.clickAndCollectAddress && this.props.config.clickAndCollectAddress.map((address: IClickAndCollectAddress, index: number) => {
                                const selected = selectedStore === (index + 1);
                                return (
                                    <React.Fragment key={index}>
                                        <option aria-selected='false' value={index + 1} selected={selected}>{address.storeName}</option>
                                    </React.Fragment>
                                );
                            })}
                        </select>
                    </div>
                </div>
            );
        }
        return null;
    }
    private _renderDeliveryNotes =(): JSX.Element => {
        const {selectedOption, deliveryNotes} = this.state;
        return (
            <div className={`delivery-notes ${selectedOption}`}>
                {/* {(selectedOption === deliveryType.delivery || selectedOption === deliveryType.express) && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update || this.currentOperation === AddressOperation.List) && ( */}
                {(selectedOption === deliveryType.delivery) && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update || this.currentOperation === AddressOperation.List) && (
                <textarea
                    ref={this.deliveryNotesRef}
                    aria-label='Delivery Notes'
                    placeholder={this.props.resources.deliveryNotesPlaceHolderText}
                    className='deliveryNotes-textarea'
                    value={deliveryNotes}
                    onChange={this._onEditDeliveryNotes}
                />)}
            {/* {(selectedOption === deliveryType.delivery || selectedOption === deliveryType.express) && this.currentOperation === AddressOperation.Show && */}
            {(selectedOption === deliveryType.delivery) && this.currentOperation === AddressOperation.Show &&
                <div className='checkout-delivery-options__deliveryNotes'>
                    {deliveryNotes}
                </div>
            }
            </div>
        );
    }
    // tslint:disable-next-line:no-any
    private _onOptionChange = (option: string) => (event: any) => {
        const {telemetry} = this.props;
        if (event.type === 'click' || ((event.type === 'keydown') && (event.keyCode === KeyCodes.Enter))) {
            option = option.toLocaleLowerCase();
            switch (option) {
                case deliveryType.delivery: {
                    this.setState({ selectedOption: deliveryType.delivery });
                    this._updateSelectedShippingType('SHIPPING').catch(error => telemetry.error(`error in _onOptionChange ${error}`));
                    this._updateCurrentOperation(AddressOperation.Add)
                    break;
                }
                case deliveryType.clickAndCollect: {
                    this.setState({ selectedOption: deliveryType.clickAndCollect, deliveryNotes: '' });
                    this._updateSelectedShippingType('COLLECT').catch(error => telemetry.error(`error in _onOptionChange ${error}`));
                    if (this.addUpdateAddress.Name === undefined && this.contactName) {
                        set(this.addUpdateAddress, {
                            ['Name']: this.contactName
                        });
                    }
                    break;
                }
                // case deliveryType.express: {
                //     this.setState({ selectedOption: deliveryType.express });
                //     this._updateSelectedShippingType('EXPRESS').catch(error => telemetry.error(`error in _onOptionChange ${error}`));
                //     console.log('this', this.props.data.checkout.result?.checkoutCart.cart.ChargeAmount);
                //     break;
                // }
                default: {
                    option = option.toLocaleLowerCase();
                }
            }
        }
    }
    private _onEditDeliveryNotes = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const textArea: HTMLTextAreaElement | null = this.deliveryNotesRef && this.deliveryNotesRef.current && this.deliveryNotesRef.current;
        const deliveryNotes = this.deliveryNotesRef && this.deliveryNotesRef.current && this.deliveryNotesRef.current.value || '';
        const text: string = deliveryNotes;
        textArea!.value = text;
        this.setState({ deliveryNotes: text });
    }

    private _getStoreAddress = (): IClickAndCollectAddress => {
        const { config } = this.props;
        const storeSelectValue = this._storePickerSelectRef.current && this._storePickerSelectRef.current.value && parseInt(this._storePickerSelectRef.current.value, 10) || 0;
        const storeAddresses: IClickAndCollectAddress[] = config.clickAndCollectAddress || [];
        const storeAddress = storeAddresses && storeAddresses[storeSelectValue - 1] || [];
        this.lastStoreName = this.state.selectedStoreName;
        storeAddress && this.setState({
            selectedStoreName: storeAddress.storeName || '',
            selectedStoreCode: storeAddress.storeIdCode
        });
        return storeAddress;
    }
    private _onAddressChange = (option: string) => (e?: React.ChangeEvent<HTMLSelectElement>) => {
        const {name} = this.state;
        const storeAddress: IClickAndCollectAddress = this._getStoreAddress();
        this._updateAddress(option, storeAddress, name);
    }
    private _updateAddress = (option: string, clickAndCollectAddress?: IClickAndCollectAddress, firstName?: string) => {
        // const { data } = this.props;
        const { customerDefaultAddress } = this.state;
        let address: Address = this._getAddress(option, clickAndCollectAddress, firstName);
        const extensionProperties = address.ExtensionProperties;
        const storeSelected = this._storePickerSelectRef.current && this._storePickerSelectRef.current.value && parseInt(this._storePickerSelectRef.current.value, 10) || 0;
        this.lastStoreCode = this.state.selectedStore;

        if (option.toLocaleLowerCase() === deliveryType.delivery && address.City === '') {
            address = customerDefaultAddress;
            address.ExtensionProperties = extensionProperties;
        }
        this.addUpdateAddress = address;
        this.setState({selectedStore: storeSelected});
        // this.setState({selectedOption: deliveryType.clickAndCollect});
        // data && data.checkout && data.checkout.result && data.checkout.result.updateShippingAddress({ newShippingAddress: address });
    }

    private _getDefaultCountryRegionId(): string {
        const { channelConfiguration } = this.props.data;
        return channelConfiguration.result && channelConfiguration.result.CountryRegionId || 'NZL';
    }

    // tslint:disable-next-line:cyclomatic-complexity
    private _getAddress = (option: string, clickAndCollectAddress?: IClickAndCollectAddress, firstName?: string): Address => {
        const { context } = this.props;
        let name = '';
        if (firstName) {
            name = firstName;
        } else {
            if (context && context.request && context.request.user && context.request.user.isAuthenticated) {
                name = context.request.user.name || '';
            } else {
                name = this._getContactNameFromCartExtensions();
            }
        }

        const threeLetterISORegionName = this._getDefaultCountryRegionId();
        const state = threeLetterISORegionName;
        let addressTypeValue = '';
        let address: Address = {
            Name: '',
            Street: '',
            StreetNumber: '',
            County: '',
            City: '',
            ThreeLetterISORegionName: threeLetterISORegionName,
            ZipCode: '',
            Phone: ''
        };

        if (clickAndCollectAddress) {
            address = {
                Name: name,
                Street: clickAndCollectAddress.street || '',
                StreetNumber: clickAndCollectAddress.streetNumber || '',
                City: clickAndCollectAddress.city || '',
                State: clickAndCollectAddress.threeLetterCountryCode || state,
                ThreeLetterISORegionName: clickAndCollectAddress.threeLetterCountryCode || threeLetterISORegionName,
                ZipCode: clickAndCollectAddress.zipCode || '',
                Phone: clickAndCollectAddress.phone || ''
            };
            if (clickAndCollectAddress.buildingCompliment && clickAndCollectAddress.buildingCompliment.length > 0) {
                address.BuildingCompliment = clickAndCollectAddress.buildingCompliment;
            }
            if (clickAndCollectAddress.postBox && clickAndCollectAddress.postBox.length > 0) {
                address.Postbox = clickAndCollectAddress.postBox;
            }
            if (clickAndCollectAddress.district && clickAndCollectAddress.district.length > 0) {
                address.DistrictName = clickAndCollectAddress.district;
            }
        }

        if (option === deliveryType.delivery) {
            addressTypeValue = 'SHIPPING';
        } else if (option === deliveryType.clickAndCollect) {
            addressTypeValue = 'COLLECT';
        }

        address.ExtensionProperties = address.ExtensionProperties || [];

        const property = address.ExtensionProperties.find((extension) => extension.Key === 'ADDRESSTYPE');
        if (property) {
            property.Value = { StringValue: addressTypeValue };
        } else {
            address.ExtensionProperties.push({
                Key: 'ADDRESSTYPE',
                Value: {
                    StringValue: addressTypeValue
                }
            });
        }

        return address;
    }

    // tslint:disable-next-line: cyclomatic-complexity
    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        const { context, data } = this.props;
        const {selectedOption} = this.state;
        const cityList = this.props.config.cityList;

        const addressFormat = this.currentOperation === AddressOperation.Add &&
            this.addressCommon.isAuthenticatedFlow() ? this.addressFormat : this.addressFormat;

        let addressFormatItems = addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        this.isNZ = this.addUpdateAddress.ThreeLetterISORegionName === this.nzlCountryRegionId;
        this.addressDropdownsData = addressFormat.getPrefilledAddressDropdownData(
            this.resources.addressStateDefaultSelectionText,
            this.stateProvinceInfo
        );
        let name = '';
        if (context && context.request && context.request.user && context.request.user.isAuthenticated) {
            name = context.request.user.name || '';
        } else {
            if (data.checkout && data.checkout.result && data.checkout.result.shippingAddress?.Name) {
                name = data.checkout.result.shippingAddress.Name;
            } else {
                name = this._getContactNameFromCartExtensions();
            }
        }
        let address: Address | undefined = data.checkout && data.checkout.result && data.checkout.result.shippingAddress ||
            this.selectedAddress || { Name: name, ThreeLetterISORegionName: this.countryRegionId };
        const property = address && address.ExtensionProperties &&
            address.ExtensionProperties.find((extension) => extension.Key === 'ADDRESSTYPE');
        const propertyValue = property && property.Value && property.Value.StringValue || '';
        let phone = '';
        if (propertyValue === 'SHIPPING_EDIT' || this.currentOperation === AddressOperation.Add) {
            phone = this._getContactPhoneFromCartExtensions();
            address = { Name: name, Phone: phone, ThreeLetterISORegionName: this.countryRegionId };
        }
        if (this.isNZ && !this.manualAddress) {
            addressFormatItems = addressFormatItems.filter(item => item.name === 'Name' || item.name === 'Phone');
        }
        if (selectedOption === deliveryType.clickAndCollect) {
            addressFormatItems = addressFormatItems.filter(item => item.name === 'Name');
        }
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: addressFormatItems,
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            hasError: this.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: this.addressDropdownsData,
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            onSave: this.onSubmit,
            onCancel: !this.selectedAddress && !this.addressCommon.isAuthenticatedFlow() ? () => undefined : this._resetView,
            searchAddress: this._renderSearchAddress
        }, cityList);
    };

    private _getContactNameFromCartExtensions = (): string => {
        const firstName = this._getExtensionPropertyValue('FIRSTNAME') || '';
        const lastName = this._getExtensionPropertyValue('LASTNAME') || '';
        return `${firstName} ${lastName}`;
    }
    private _getContactPhoneFromCartExtensions = (): string => {
        const {checkout} = this.props.data;
        const cart = checkout.result?.checkoutCart.cart;
        const phoneProperty = cart && cart.ExtensionProperties && cart.ExtensionProperties.find((extension: CommerceProperty) => extension.Key === 'PHONENUMBER');
        const phone = phoneProperty && phoneProperty.Value && phoneProperty.Value.StringValue || '';
        return `${phone}`;
    }
    private _getExtensionPropertyValue = (fieldName: string) => {
        const value = this.props.data.checkout.result?.checkoutCart?.cart.ExtensionProperties &&
            this.props.data.checkout.result?.checkoutCart?.cart.ExtensionProperties?.filter(p=>p.Key === fieldName);
        if (value && value?.length > 0) {
            return value[0].Value?.StringValue || undefined;
        }

        return undefined;
    }

    private _onSave = async (status: string): Promise<void> => {
        await getAddressSaveInput(createAddressSaveInput(status), this.props.context.actionContext);
    };

    private _renderSelectAddress = (): IAddressSelectProps => {
        return AddressSelect({
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            resources: this.resources,
            addressPurposes: this.addressPurposes,
            selectedAddress: this.addressListSelectedAddress,
            onAddressOptionChange: this._onAddressOptionChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            telemetryContent: this.telemetryContent,
            onAddAddress: this._goToAddAddress,
            onSave: this._onSelectAddress,
            onCancel: !this.selectedAddress ? () => undefined : this._resetView
        });
    };

    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({
                address: this.selectedAddress,
                addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || ''),
                addressPurposes: this.addressPurposes
            });
        }

        return null;
    }
    private _updateSelectedShippingType = async (option: string): Promise<void> => {

        await getShippingTypeInput(craeteShippingTypeInput(option), this.props.context.actionContext);
    }
    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    }

    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        },         0);

    }

    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        addressFormatItem.forEach(format => {
            if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                this.addressFormat[format.name] = '';
            }
        });
        this._clearValidation();
    }

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    }

    private _onAddressOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const addressRecordId = event.currentTarget.value;
        const selectedAddress = this.customerAddresses.find(address => (address.RecordId || '').toString() === addressRecordId);
        if (selectedAddress) {
            this.addressListSelectedAddress = selectedAddress;
        }
    };

    private onAddressAddUpdate = (name: string, value: string | boolean) => {
        const cityList = this.cityList();
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name, cityList);
        if (name.toLocaleLowerCase() === 'name') {
            this.setState({name: value.toString()});
        }
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };
    private _onAddressAddUpdateSubmit = async (): Promise<void> => {
        const cityList = this.cityList();
        if (await this.isRegionAU6and7(this.addUpdateAddress.ZipCode)) {
            this.setState({ regionAU6or7: true });
        } else {
            this.setState({ regionAU6or7: false });
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId,undefined,cityList)) {
            this.manualAddress = true;
            return;
        }
        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow() && (this.state.selectedOption.toLocaleLowerCase() !== deliveryType.clickAndCollect)) {
            set(this.addUpdateAddress, { ['AddressTypeValue']: 2 });
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this.isUpdating = true;
        this._updateModuleState();

        response.then(
            (result: IAddressResponse) => {
                this.isUpdating = false;

                if (result.address) {
                    this.hasError = false;
                    this._onAddOrUpdateSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                    this._updateModuleState();
                }
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
                this._updateModuleState();
            }
        );
        }
    };

    private updateCartDeliverySpecification = async (deliveryModeId: string): Promise<void> => {
        if (deliveryModeId.trim() === '') {
            return;
        }

        const { data, resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        const address: Address = data.checkout.result!.shippingAddress || this._getShippingAddressFromCartLines() || this.addUpdateAddress || this.selectedAddress || {};
        address.ExtensionProperties = [];

        const updateCartDeliverySpecificationInput = {
            deliveryModeId: deliveryModeId,
            shippingAddress: address
        };

        this.clearError();
        setTimeout(async () => {
            if(checkoutState.checkoutCart.status === 'READY' || this.addressCommon.isAuthenticatedFlow()) {
                await checkoutState.checkoutCart.updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
                    .then((response) => {
                        if (response.status === 'FAILED') {
                            this.setError(resources.genericErrorMessage);
                        } else {
                            this.props.moduleState.setIsCancellable(true);
                        }
                    })
                    .catch(
                        () => {
                            this.setError(resources.genericErrorMessage);
                        }
                    );
            }
        },         0);

    }

    private _updateCartDeliverySpecification = (): void => {
        const { data, config } = this.props;
        const {selectedOption} = this.state;
        const DeliveryAddress = data.checkout.result!.shippingAddress || this._getShippingAddressFromCartLines() || this.addUpdateAddress || this.selectedAddress || {};

        if (this._isValidAddress(DeliveryAddress)) {
            let deliveryOptionMode = config.deliveryOption1Mode || '';
            if (selectedOption === deliveryType.delivery) {
                deliveryOptionMode = config.deliveryOption1Mode || '';
                this.setState({
                    selectedStoreName: '',
                    selectedStoreCode: ''
                });
            } else if (selectedOption === deliveryType.clickAndCollect) {
                deliveryOptionMode = config.deliveryOption2Mode || '';}
            // } else if (selectedOption === deliveryType.express) {
            //     deliveryOptionMode = config.deliveryOption3Mode || '';
            //     this.setState({
            //         selectedStoreName: '',
            //         selectedStoreCode: ''
            //     });
            // }
            this.updateCartDeliverySpecification(deliveryOptionMode).catch((error: Error) => {
                throw error;
            });
        }
    }
    private _isValidAddress = (address: Address): boolean => {
        const { resources } = this.props;
        if (address) {
            if (address.Name && address.Street && address.City && address.ThreeLetterISORegionName && address.ZipCode) {
                return true;
            }
        }
        address.Name && !address.City && this.setError(resources.addressValidationErrorMessage);
        return false;
    }

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName =  this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    };
    private clearError = (): void => {
        this.props.moduleState.setHasError(false);
    };
    private setError = (errorMessage: string): void => {
        const {telemetry, moduleState } = this.props;
        telemetry.error(errorMessage);
        moduleState.setHasError(true);
        moduleState.onUpdating();
    };
    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    }

    private _getStateProvinces = (): void => {
        const {telemetry} = this.props;
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { ['State']: '' });
            }

            this.stateProvinceInfo = result;
        }).catch(error => telemetry.error(`error in _getStateProvinces ${error}`));
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const isB2BI = request && request.apiSettings && request.apiSettings.oun === "B2BI"
        const market = isB2BI ? "US" : request && request.channel && request.channel.ChannelCountryRegionISOCode;
        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _getDefaultAddress = (): Address | undefined => {
        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
        }
        return undefined;
    };

    private _initModuleState = async (): Promise<void> => {
        this.props.moduleState.init({
            status: this._canShip() ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        if (this._canShip()) {
            const defaultAddress = this._getShippingAddressFromCartLines() || this._getDefaultAddress();
            if (defaultAddress && !this.addressCommon.isEmpty(defaultAddress)) {
                if (await this.isRegionAU6and7(defaultAddress.ZipCode)) {
                    this._updateCurrentOperation(AddressOperation.List, defaultAddress);
                } else {
                this._updateCurrentOperation(AddressOperation.Show, defaultAddress);
                this._setShippingAddress(defaultAddress, true);
                this._updateModuleState();
                }
            } else {
                this._updateCurrentOperation(AddressOperation.Add);
            }
        }
    };

    private cityList = ()=> {
        const citylist = this.props.config.cityList;
        return citylist;
    }
    
    private isRegionAU6and7 = async (postalCodeInput?: string): Promise<boolean> => {
        const response: IDXC_GetFreightChargesServiceResponse = await this._getRegionID(postalCodeInput);
        const market = this.props.context?.request?.channel?.ChannelCountryRegionISOCode;
        const isAU = market === "AU";
        const isB2B = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
        if (isB2B && isAU && response && response.Result) {
            let messageStr = response.Message;
            if (messageStr && (messageStr.includes('RegionAU6') || messageStr.includes('RegionAU7'))) {
                return true
            }
        }
        return false;
    }
    private _getRegionID(postalCode?: string): Promise<IGetFreightChargesServiceResponse> {
        const ctx: IContext = { callerContext: this.props.context.actionContext };
        const response: IGetFreightChargesServiceResponse = { ChargesAmount: 0 };
        const itemLine: IDXC_ItemLine = { ItemId: '', Quantity: 1 };
        if (postalCode) {
            return dXC_GetFreightChargesForPDPAsync(ctx, postalCode, itemLine);
        }
        return Promise.resolve(response);
    }
    private onSubmit = (): void => {
        // debugger;
        if (this.state.selectedOption.toLocaleLowerCase() === deliveryType.delivery) {
            this.setState({selectedStoreCode:'', selectedStore:0, selectedStoreName:''});
        } else if (this.state.selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect) {
            this.setState({deliveryNotes: ''});
        }
        const isAuthClickAndCollect = (this.state.selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect) && (this.addressCommon.isAuthenticatedFlow());
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._onAddressAddUpdateSubmit();
                break;
            case AddressOperation.List:
                isAuthClickAndCollect ? this._onAddressAddUpdateSubmit() : this._onSelectAddress();
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._clearAddressFields();
                if (!(!this.selectedAddress && !this.addressCommon.isAuthenticatedFlow())) {
                    this.setState({
                        selectedStoreName: this.lastStoreName || '',
                        selectedStore: this.lastStoreCode,
                        deliveryNotes: this._getExtensionPropertyValue('DELIVERYNOTES') || ''
                    });
                    this._resetView();
                }

                break;
            case AddressOperation.List:
                if (this.selectedAddress) {
                    this.setState({
                        selectedStoreName: this.lastStoreName || '',
                        selectedStore: this.lastStoreCode,
                        deliveryNotes: this._getExtensionPropertyValue('DELIVERYNOTES') || ''
                    });
                    this._resetView();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onEdit = (): void => {
        if (this.addressCommon.isAuthenticatedFlow() && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.List, this.shippingAddress);
        } else if (this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.shippingAddress);
        }

        this._updateModuleState();
    };

    private _getShippingAddressFromCartLines = (): Address | undefined => {
        const { request } = this.props.context;
        const { channelDeliveryOptionConfig } = this.props.data;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        const cartLines = this.retailMultiplePickUpOptionEnabled ?
                ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(
                    cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
                    ? (cartLine.DeliveryMode !== channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode)
                    : cartLine
                ):
                ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(
                    cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
                    ? (cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode)
                    : cartLine
                );

        if (cartLines && cartLines.length > 0) {
            return cartLines[0].ShippingAddress;
        }

        return undefined;
    };

    private _canShip = (): boolean => {
        const { checkout, channelDeliveryOptionConfig } = this.props.data;
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        if (!checkout.result || !request.channel || checkout.result.checkoutCart.isEmpty || checkout.result.checkoutCart.hasInvoiceLine) {
            return false;
        }

        // @ts-ignore: Type-checker not realizing above request.channel check
        return this.retailMultiplePickUpOptionEnabled?
        checkout?.result?.checkoutCart?.cart?.CartLines?.some(
            cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
            ? (cartLine.DeliveryMode !== channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode)
            : cartLine
        )
        : checkout?.result?.checkoutCart?.cart?.CartLines?.some(
            cartLine => (cartLine.DeliveryMode && cartLine.DeliveryMode !== '')
                ? (cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode)
                : cartLine
        );
    };

    private _renderRegionAUError(): React.ReactNode | null {
        const { config } = this.props
        const { regionAU6or7 } = this.state;
        const errorMessage = config.regionAU6or7ErrorMessage || "Your address is not delievried by us, please choose a new one";
        if (regionAU6or7) {
            return (
                <div className='checkout-regionAU6or7'>
                    <span className='checkout-regionAU6or7-erroMessage' style={{color : 'red'}}>{errorMessage}</span>
                </div>
            );
        } else {
            return null
        }
    }

    private _onSelectAddress = async () => {
        const {telemetry} = this.props;
        const {name, selectedStore, selectedOption} = this.state;
        if (selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect && this.addressCommon.isAuthenticatedFlow()) {
            this.addressListSelectedAddress.Name = name;
        }
        if(selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect && selectedStore < 1) {
            return;
        }
        if (await this.isRegionAU6and7(this.addressListSelectedAddress.ZipCode)) {
            this.setState({ regionAU6or7: true });
        } else {
            this.setState({ regionAU6or7: false });
        this._updateCurrentOperation(AddressOperation.Show, this.addressListSelectedAddress);
        this._setShippingAddress(this.addressListSelectedAddress);
        this._updateModuleState();
        this._onSave(`success-${new Date().getTime()}`).catch(
            error => telemetry.error(`error in _onSelectAddress at _onSave ${error}`));
        }
    };

    private _goToAddAddress = () => {
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = (response: IAddressResponse) => {
        const {telemetry} = this.props;
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }

        if (response.address) {
            this._updateCurrentOperation(AddressOperation.Show, response.address);
            this._setShippingAddress(response.address);
            this._updateModuleState();
            this._onSave(`success-${new Date().getTime()}`).catch(
                error => telemetry.error(`error in _onAddOrUpdateSuccess at _onSave ${error}`));
        }
    };

    private _setShippingAddress = (address: Address, isInitMode: boolean = false): void => {
        if (this.props.data.checkout.result) {
            const {telemetry} = this.props;
            const newShippingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newShippingAddress.TwoLetterISORegionName) {
                newShippingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }

            newShippingAddress.AddressTypeValue = newShippingAddress.AddressTypeValue || 2; // Sets it to Delivery by default
            // update the delivery lines
            // this.props.data.checkout.result.updateShippingAddress({ newShippingAddress })
            //     .catch(error => telemetry.error(`error in _setShippingAddress ${error}`));
            
            this._updateExtensionProps(isInitMode).then((res) => {
                
                this.props.data.checkout.result && this.props.data.checkout.result.updateShippingAddress({ newShippingAddress })
                .catch(error => telemetry.error(`error in _setShippingAddress ${error}`))

                    this._updateCartDeliverySpecification();
                    }
                ).catch(error => telemetry.error(`error in _updateExtensionProps ${error}`));
        }
    };

    private _updateExtensionProps= async(isInitMode: boolean) => {
        const countryISOCode = this.props.data.channelConfiguration?.result?.ChannelCountryRegionISOCode;
        if(!isInitMode) {
            const {deliveryNotes, selectedOption, selectedStoreCode, selectedStore, selectedStoreName} = this.state;
    
            if (countryISOCode === 'NZ') {
                // Save deliveryNotes and delivery Option in the extension property
                await this.props.data.checkout.result?.checkoutCart.updateExtensionProperties({
                    newExtensionProperties: [
                            {Key:'DELIVERYNOTES', Value: {StringValue: deliveryNotes }},
                            {Key:'DELIVERYTYPE', Value: {StringValue: selectedOption }},
                            {Key:'SELECTEDSTOREINDEX', Value: {StringValue: selectedStore.toString() }},
                            {Key:'SELECTEDSTORENAME', Value: {StringValue: selectedStoreName }},
                            {Key:'SELECTEDSTORECODE', Value: {StringValue: selectedStoreCode }},
                            {Key:'DeliveryOption', Value: {StringValue: selectedOption }},
                            {Key:'SelectedStore',  Value: {StringValue: selectedStore.toString() }},
                            {Key:'StoreCode', Value: {StringValue: selectedStoreCode }}
                        ]
                });

            } else {
                // FIX CAS-177772-Z7R0: Remove store data from non NZ order
                await this.props.data.checkout.result?.checkoutCart.updateExtensionProperties({
                    newExtensionProperties: [
                        {Key:'DELIVERYNOTES', Value: {StringValue: deliveryNotes }},
                        {Key:'DELIVERYTYPE', Value: {StringValue: selectedOption }},
                        {Key:'DeliveryOption', Value: {StringValue: selectedOption }},
                    ]
                });
            }
        }
    };

    @computed get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress;
    }

    private _resetView = (): void => {
        const shippingTypeExtValue: string = this._getExtensionPropertyValue('DELIVERYTYPE') || '';
        const storeName: string = this._getExtensionPropertyValue('SELECTEDSTORENAME') || '';
        const shippingAddress: Address | undefined = this._getShippingAddressFromCartLines();
        if (!isNullOrWhitespace(shippingTypeExtValue)) {
            this.setState({selectedOption: shippingTypeExtValue});
        }
        if (!isNullOrWhitespace(storeName)) {
            this.setState({selectedStoreName: storeName});
        }
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                {
                    this.showAddressSearch = true;
                    this._updateCurrentOperation(
                        this.addressCommon.isAuthenticatedFlow() ? AddressOperation.List : AddressOperation.Show,
                        this.shippingAddress !== shippingAddress ? shippingAddress: this.shippingAddress
                    );
                }
                break;
            default:
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress !== shippingAddress ? shippingAddress: this.shippingAddress);
        }
        this._updateModuleState();
    };

    private _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        const {selectedOption} = this.state;
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (selectedOption.toLocaleLowerCase() === deliveryType.clickAndCollect &&
            this.addressCommon.isAuthenticatedFlow() && isNullOrWhitespace(this.addUpdateAddress.Name || '')) {
            const shippingAddress: Address = this._getShippingAddressFromCartLines() || {};
            this.shippingName = shippingAddress.Name || '';
            this.contactName = this._getContactNameFromCartExtensions();
            const name = this.shippingName || this.contactName;
            this.setState ({name: name});
            set(this.addUpdateAddress, { ['Name']: name });
            if (this.selectedAddress) {
                this.selectedAddress.Name = name;
            }
        }

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = { ...this.selectedAddress } || {};
            const contactPhone = this._getExtensionPropertyValue('PHONENUMBER');
            if (this.addUpdateAddress.Name === undefined && this.contactName) {
                set(this.addUpdateAddress, {
                    ['Name']: this.contactName
                });
            }
            if (this.addUpdateAddress.Phone === undefined && contactPhone) {
                set(this.addUpdateAddress, {
                    ['Phone']: contactPhone
                });
            }
            set(this.addUpdateAddress, {
                ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            });
            set(this.addUpdateAddress, {
                ['AddressTypeValue']: this.addUpdateAddress.AddressTypeValue || this.defaultAddressType
            });
            this.showAddressSearch = true;
        } else if (this.currentOperation === AddressOperation.List) {
            this.addressListSelectedAddress = { ...this.selectedAddress } || {};
            this.showAddressSearch = false;
        }
    };

    private _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
            this.showAddressSearch = false;
        } else if (this.isUpdating) {
            this.props.moduleState.onPending();
            this._enableAdressSearch();
        } else {
            this.props.moduleState.onUpdating();
            if(this.currentOperation !== AddressOperation.List) {
                this._enableAdressSearch();
            }
        }
    };
    private _enableAdressSearch = (): void => {
        this.showAddressSearch = true;
    }
    private _toggleManualAddressInput = (): void => {
        this.manualAddress = !this.manualAddress;
    }
    /**
     * callback function for receiving address details from address search component
     */
    private _handleSearchAddressSelection = (address: IFormattedAddressDetails): void => {
        // check fields
        if (!(address && address.country && address.city && address.postcode && address.street && address.suburb)) {
            this.addUpdateAddress = {
                Name: this.addUpdateAddress.Name,
                Phone: this.addUpdateAddress.Phone,
                ThreeLetterISORegionName: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            };
            return;
        }
        // cache name and phone in case of country change
        const name = this.addUpdateAddress.Name;
        const phone = this.addUpdateAddress.Phone;
        // country update - needs 3 letter iso
        const countryCode = this._getCountryThreeLetterCode(address.country);
        if (!countryCode) {
            this.props.telemetry.error('country code undefined for ', address.country);
            return;
        }
        this._elementUpdate('ThreeLetterISORegionName', countryCode);
        // save other address fields
        this._elementUpdate('City', address.city);
        this._elementUpdate('ZipCode', address.postcode);
        this._elementUpdate('Street', address.street);
        this._elementUpdate('StreetNumber', address.street_number ? address.street_number : '');
        this._elementUpdate('BuildingCompliment', address.building_name ? address.building_name : '');
        this._elementUpdate('DistrictName', address.suburb);
        // restore name & phone
        if (name) {
            this._elementUpdate('Name', name);
        }
        if (phone) {
            this._elementUpdate('Phone', phone);
        }
    }
    private _elementUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    }
    private _getCountryThreeLetterCode = (country: string): string | undefined => {
        const dropdownDisplayData = this.addressDropdownsData.ThreeLetterISORegionName;
        const data = dropdownDisplayData.find((item) => item.value === country);
        return data ? data.key?.toString() : undefined;
    }
    public async getProductDetails(cartLines: CartLine[], channelId: number, context: IActionContext): Promise<IHSOProductDetails[]> {
        let i = 0;
        let productsInventory: IHSOProductDetails[] = [];
        for (i = 0; i < cartLines.length; i++) {
            let element: IHSOProductDetails = { productDetails: null, AttributeValues: null };
            if (cartLines[i] !== undefined) {
                const responseHSOInterface = await _getProductDetailsWithAttributes(cartLines[i].ProductId!, channelId, context, context.requestContext);
                if (responseHSOInterface?.productDetails) {
                    element.productDetails = responseHSOInterface?.productDetails;
                }
                if (responseHSOInterface?.AttributeValues) {
                    element.AttributeValues = responseHSOInterface?.AttributeValues;
                }
                
                productsInventory.push(element);
            }
        }
        return productsInventory;
    }
}

export default CheckoutShippingAddress;
